import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_STRING as api } from '@/App';
import { toast } from 'sonner';

import { logger } from '@/utils/logger';
import { ArrowLeft, Heart, Loader2 } from 'lucide-react';
import ProductCard from '@/components/ProductCard';
import { Button } from '@/components/ui/button';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

export default function SharedWishlist() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dynamic OG tags so social crawlers (iMessage, WhatsApp, Twitter,
  // Discord) render a preview specific to *this* wishlist, not the
  // generic home-page card baked into index.html.
  const ownerName = wishlist?.owner_name || wishlist?.user_name || 'A friend';
  const wishlistName = wishlist?.name || 'Wishlist';
  useDocumentMeta({
    title: wishlist
      ? `${ownerName}'s ${wishlistName}, Mementa`
      : 'Shared Wishlist, Mementa',
    description: wishlist
      ? `${ownerName} shared a wishlist with you on Mementa. See what they're hoping for.`
      : 'View a wishlist shared with you on Mementa.',
    type: 'website',
  });

  const fetchSharedWishlist = useCallback(async () => {
    try {
      const response = await fetch(`${api}/w/${shareId}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Wishlist not found or is private');
        } else {
          setError('Failed to load wishlist');
        }
        setLoading(false);
        return;
      }

      const data = await response.json();
      setWishlist(data);
    } catch (err) {
      logger.error('Error fetching shared wishlist:', err);
      setError('Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  }, [shareId]);

  useEffect(() => {
    fetchSharedWishlist();
  }, [fetchSharedWishlist]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: '#9E6752' }} />
          <p className="text-gray-600">Loading wishlist...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <Heart className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h1 className="text-2xl font-bold mb-2" style={{ color: '#20212B' }}>Wishlist Not Found</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button 
            onClick={() => navigate('/')}
            style={{ background: '#9E6752', color: 'white' }}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Browse Products
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 page-enter">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm mb-4 hover:underline"
            style={{ color: '#73766A' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Browse All Products
          </button>
          
          <div className="flex items-center gap-3">
            <Heart className="w-8 h-8" style={{ color: '#9E6752' }} />
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: '#20212B' }}>
                {wishlist?.owner_name}'s Wishlist
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {wishlist?.products?.length || 0} {wishlist?.products?.length === 1 ? 'item' : 'items'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {wishlist?.products?.length === 0 ? (
          <div className="text-center py-16">
            <Heart className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h2 className="text-xl font-semibold mb-2" style={{ color: '#20212B' }}>
              No Items Yet
            </h2>
            <p className="text-gray-600 mb-6">
              {wishlist?.owner_name} hasn't added any items to their wishlist yet.
            </p>
            <Button 
              onClick={() => navigate('/')}
              style={{ background: '#9E6752', color: 'white' }}
            >
              Browse Products
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {wishlist?.products?.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onQuickView={() => {}}
                isInWishlist={false}
                onWishlistToggle={() => toast.info('Create an account to save items to your wishlist')}
              />
            ))}
          </div>
        )}
      </div>

      {/* CTA Footer */}
      <div className="bg-white border-t mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <h3 className="text-2xl font-bold mb-3" style={{ color: '#20212B' }}>
            Create Your Own Wishlist
          </h3>
          <p className="text-gray-600 mb-6">
            Save your favorite gifts and share them with friends and family
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              onClick={() => navigate('/signup')}
              style={{ background: '#2D4354', color: 'white' }}
            >
              Sign Up Free
            </Button>
            <Button 
              onClick={() => navigate('/login')}
              variant="outline"
              style={{ borderColor: '#9E6752', color: '#9E6752' }}
            >
              Log In
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
